/* =============================================================================
   PNH BUNDLE BUILDER — JavaScript
   Mix & Match Personalized Care Collection Experience
   ============================================================================= */

(function () {
  'use strict';

  /* ---------------------------------------------------------------------------
     BundleBuilder — main controller
     --------------------------------------------------------------------------- */
  class BundleBuilder {
    constructor(el) {
      this.el = el;
      this.sectionId = el.dataset.sectionId;
      // Translated UI strings injected by the section via a JSON script tag;
      // every t() call falls back to the built-in English string.
      this.i18n = {};
      const i18nEl = document.querySelector('[data-pnh-bb-i18n]');
      if (i18nEl) {
        try { this.i18n = JSON.parse(i18nEl.textContent) || {}; } catch (e) { this.i18n = {}; }
      }
      this.t = (key, fallback) => (typeof this.i18n[key] === 'string' ? this.i18n[key] : fallback);
      // Disabled milestones are dropped at parse time so every downstream
      // helper sees an "enabled-only" list. Sort by count so getNextMilestone()
      // and getProgressPercent() stay correct even if the admin enters counts
      // out of order across the four slots.
      this.milestones = JSON.parse(el.dataset.milestones || '[]')
        .filter(m => m.enabled !== false)
        .sort((a, b) => a.count - b.count);
      this.bundle = new Map(); // variantId (number) → item object
      this.activeFilter = 'all';
      this.previousItemCount = 0;

      this.dom = {
        grid:          el.querySelector('.pnh-bb-grid'),
        cards:         Array.from(el.querySelectorAll('.pnh-bb-card')),
        tabs:          Array.from(el.querySelectorAll('.pnh-bb-tab')),
        emptyState:    el.querySelector('.pnh-bb-empty'),
        sidebarItems:  document.querySelector('.pnh-bb-sidebar-items'),
        sidebarEmpty:  document.querySelector('.pnh-bb-sidebar-empty'),
        sidebarCount:  document.querySelector('.pnh-bb-sidebar-count'),
        sidebarTotals: document.querySelector('.pnh-bb-sidebar-subtotal-value'),
        sidebarChkout: document.querySelector('.pnh-bb-sidebar .pnh-bb-checkout-btn'),
        sidebarClear:  document.querySelector('.pnh-bb-sidebar .pnh-bb-clear-btn'),
        drawer:        document.querySelector('.pnh-bb-drawer'),
        drawerOverlay: document.querySelector('.pnh-bb-drawer-overlay'),
        drawerItems:   document.querySelector('.pnh-bb-drawer-items'),
        drawerTotals:  document.querySelector('.pnh-bb-drawer-totals'),
        drawerChkout:  document.querySelector('.pnh-bb-drawer-checkout'),
        drawerClose:   document.querySelector('.pnh-bb-drawer-close'),
        drawerToggle:  document.querySelector('.pnh-bb-drawer-toggle'),
        toggleCount:   document.querySelector('.pnh-bb-drawer-toggle__count'),
        togglePrice:   document.querySelector('.pnh-bb-drawer-toggle__price'),
        heroCta:       document.querySelector('.pnh-bundle-hero__cta'),
        toggleSub:     document.querySelector('.pnh-bb-drawer-toggle__sublabel'),
        routineBtns:   Array.from(el.querySelectorAll('.pnh-bb-routine__add[data-variant-ids]')),
      };
      this.freeThreshold = parseInt(el.dataset.freeThreshold, 10) || 0;
      if (this.dom.toggleSub) this.toggleSubDefault = this.dom.toggleSub.innerHTML;
      this.dom.routineBtns.forEach(btn => { btn.dataset.labelAdd = btn.textContent.trim(); });

      this.dom.tabsWrapper = el.querySelector('.pnh-bb-tabs-wrapper');
      this.dom.tabsScroll  = el.querySelector('.pnh-bb-tabs');

      this.init();
    }

    init() {
      this.restoreFromStorage();
      this.bindTabEvents();
      this.bindTabScrollGradient();
      this.bindCardEvents();
      this.bindThemeEvents();
      this.bindRoutineEvents();
      this.bindGlobalEvents();
      this.updateUI(false);
    }

    /* -------------------------------------------------------------------------
       Local Storage
       ------------------------------------------------------------------------- */
    restoreFromStorage() {
      try {
        const raw = localStorage.getItem('pnh-bundle-v2');
        if (!raw) return;
        const items = JSON.parse(raw);
        items.forEach(item => this.bundle.set(item.variantId, item));
      } catch (_) {}
    }

    persistToStorage() {
      try {
        localStorage.setItem('pnh-bundle-v2', JSON.stringify(Array.from(this.bundle.values())));
      } catch (_) {}
    }

    /* -------------------------------------------------------------------------
       Tab filtering
       ------------------------------------------------------------------------- */
    bindTabEvents() {
      this.dom.tabs.forEach((tab, index) => {
        tab.addEventListener('click', e => {
          e.preventDefault();
          this.setFilter(tab.dataset.filter, tab);
        });
        tab.addEventListener('keydown', e => {
          const tabs = this.dom.tabs;
          let next = null;
          if (e.key === 'ArrowRight') {
            next = tabs[(index + 1) % tabs.length];
          } else if (e.key === 'ArrowLeft') {
            next = tabs[(index - 1 + tabs.length) % tabs.length];
          } else if (e.key === 'Home') {
            next = tabs[0];
          } else if (e.key === 'End') {
            next = tabs[tabs.length - 1];
          }
          if (next) {
            e.preventDefault();
            next.focus();
            this.setFilter(next.dataset.filter, next);
          }
        });
      });
    }

    bindTabScrollGradient() {
      const tabs    = this.dom.tabsScroll;
      const wrapper = this.dom.tabsWrapper;
      if (!tabs || !wrapper) return;
      const update = () => {
        // RTL scrollLeft runs 0 → negative (Chrome/Firefox/Safari today), so the
        // LTR test never reached "end" in Arabic. Math.abs covers both signs.
        const atEnd = Math.abs(tabs.scrollLeft) + tabs.clientWidth >= tabs.scrollWidth - 4;
        wrapper.classList.toggle('pnh-bb-tabs--at-end', atEnd);
      };
      let _tabTicking = false;
      const throttledTabUpdate = function () {
        if (!_tabTicking) {
          window.requestAnimationFrame(function () { update(); _tabTicking = false; });
          _tabTicking = true;
        }
      };
      tabs.addEventListener('scroll', throttledTabUpdate, { passive: true });
      update();
    }

    setFilter(filter, tabEl) {
      this.activeFilter = filter;
      this.dom.tabs.forEach(t => {
        t.classList.toggle('pnh-bb-tab--active', t === tabEl);
        t.setAttribute('aria-selected', t === tabEl ? 'true' : 'false');
        t.setAttribute('tabindex', t === tabEl ? '0' : '-1');
      });
      this.filterCards(filter);
      if (tabEl) tabEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    filterCards(filter) {
      let visible = 0;
      this.dom.cards.forEach(card => {
        const tags = (card.dataset.tags || '').toLowerCase();
        const show = filter === 'all' || tags.split(',').some(t => t.trim() === filter.toLowerCase());
        card.classList.toggle('pnh-bb-card--hidden', !show);
        if (show) visible++;
      });
      if (this.dom.emptyState) {
        this.dom.emptyState.style.display = visible === 0 ? 'flex' : 'none';
      }
      const filterEmpty = document.querySelector('.pnh-bb-empty--filter');
      if (filterEmpty) filterEmpty.style.display = visible === 0 ? 'block' : 'none';
      const announcer = document.getElementById('pnh-bb-announcer');
      if (announcer && visible === 0) {
        announcer.textContent = '';
        requestAnimationFrame(() => { announcer.textContent = this.t('noProductsFilter', 'No products found for this filter.'); });
      }
    }

    /* -------------------------------------------------------------------------
       Card events
       ------------------------------------------------------------------------- */
    bindCardEvents() {
      this.dom.cards.forEach(card => {
        const addBtn   = card.querySelector('.pnh-bb-card__add');
        const qtyMinus = card.querySelector('.pnh-bb-card__qty-minus');
        const qtyPlus  = card.querySelector('.pnh-bb-card__qty-plus');

        if (addBtn)   addBtn.addEventListener('click',   () => this.toggleCard(card));
        if (qtyMinus) qtyMinus.addEventListener('click', () => this.adjustCardQty(card, -1));
        if (qtyPlus)  qtyPlus.addEventListener('click',  () => this.adjustCardQty(card, +1));
      });
    }

    toggleCard(card) {
      const vid = parseInt(card.dataset.variantId, 10);
      if (this.bundle.has(vid)) {
        this.removeItem(vid);
      } else {
        this.addItem(this.extractCardData(card));
      }
    }

    adjustCardQty(card, delta) {
      const vid = parseInt(card.dataset.variantId, 10);
      if (!this.bundle.has(vid)) return;
      const item = this.bundle.get(vid);
      const next = item.qty + delta;
      if (next <= 0) {
        this.removeItem(vid);
      } else {
        item.qty = Math.min(next, 99);
        this.bundle.set(vid, item);
        this.persistToStorage();
        this.updateUI(false);
      }
    }

    extractCardData(card) {
      return {
        variantId:    parseInt(card.dataset.variantId, 10),
        productId:    parseInt(card.dataset.productId, 10),
        title:        card.dataset.title || '',
        subtitle:     card.dataset.subtitle || '',
        price:        parseInt(card.dataset.price, 10) || 0,
        comparePrice: parseInt(card.dataset.comparePrice, 10) || 0,
        image:        card.dataset.image || '',
        imageAlt:     card.dataset.imageAlt || '',
        qty: 1,
      };
    }

    /* -------------------------------------------------------------------------
       Bundle mutation helpers
       ------------------------------------------------------------------------- */
    addItem(data) {
      this.bundle.set(data.variantId, data);
      this.persistToStorage();
      this.updateUI(true);
      this.animateCardAdd(data.variantId);
      if (this.bundle.size === 1 && window.innerWidth < 1024) {
        setTimeout(() => this.openDrawer(), 400);
      }
    }

    removeItem(vid) {
      this.bundle.delete(vid);
      this.persistToStorage();
      this.updateUI(false);
    }

    clearBundle() {
      this.bundle.clear();
      this.persistToStorage();
      this.updateUI(false);
    }

    /* -------------------------------------------------------------------------
       Bundle theme events
       ------------------------------------------------------------------------- */
    bindThemeEvents() {
      document.querySelectorAll('.pnh-bb-theme-add').forEach(btn => {
        btn.addEventListener('click', () => this.addTheme(btn));
      });
    }

    addTheme(btn) {
      const filter = btn.dataset.filter || 'all';
      const max    = parseInt(btn.dataset.max, 10) || 4;
      let added = 0;

      this.dom.cards.forEach(card => {
        if (added >= max) return;
        const tags = (card.dataset.tags || '').toLowerCase();
        const match = filter === 'all' || tags.split(',').some(t => t.trim() === filter.toLowerCase());
        if (!match) return;
        // Skip sold-out cards — the add button is disabled for unavailable variants
        const addBtn = card.querySelector('.pnh-bb-card__add');
        if (addBtn && addBtn.disabled) return;
        const vid = parseInt(card.dataset.variantId, 10);
        if (!this.bundle.has(vid)) {
          this.bundle.set(vid, this.extractCardData(card));
          added++;
        }
      });

      if (added > 0) {
        this.persistToStorage();
        this.updateUI(true);
        if (window.innerWidth < 1024) setTimeout(() => this.openDrawer(), 300);
        const tabEl = this.dom.tabs.find(t => t.dataset.filter === filter);
        if (tabEl) this.setFilter(filter, tabEl);
      }

      const orig = btn.textContent;
      btn.textContent = added > 0 ? this.t('productsAdded', '__N__ Products Added').replace('__N__', added) : this.t('alreadyIn', 'Already in Your Collection');
      btn.disabled = true;
      setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 2500);
    }

    /* -------------------------------------------------------------------------
       Ready-made routines — add every product of one routine in one tap.
       Each variant is added through its product card, so a sold-out product
       (disabled card button) is never added and prices come from the card.
       ------------------------------------------------------------------------- */
    routineIds(btn) {
      return (btn.dataset.variantIds || '').split(',').map(v => parseInt(v, 10)).filter(Boolean);
    }

    bindRoutineEvents() {
      this.dom.routineBtns.forEach(btn => {
        btn.addEventListener('click', () => this.addRoutine(btn));
      });
    }

    addRoutine(btn) {
      let added = 0;
      this.routineIds(btn).forEach(vid => {
        if (this.bundle.has(vid)) return;
        const card = this.el.querySelector(`.pnh-bb-card[data-variant-id="${vid}"]`);
        if (!card) return;
        const addBtn = card.querySelector('.pnh-bb-card__add');
        if (addBtn && addBtn.disabled) return;
        this.bundle.set(vid, this.extractCardData(card));
        added++;
      });
      if (added === 0) return;
      this.persistToStorage();
      this.updateUI(true);
      if (window.innerWidth < 1024) setTimeout(() => this.openDrawer(), 300);
    }

    updateRoutines() {
      this.dom.routineBtns.forEach(btn => {
        const ids = this.routineIds(btn);
        const all = ids.length > 0 && ids.every(vid => this.bundle.has(vid));
        btn.classList.toggle('pnh-bb-routine__add--done', all);
        btn.textContent = all ? (btn.dataset.labelIn || btn.dataset.labelAdd) : btn.dataset.labelAdd;
        btn.setAttribute('aria-pressed', all ? 'true' : 'false');
      });
    }

    /* -------------------------------------------------------------------------
       Global events (overlay, close btn, hero CTA, checkout)
       ------------------------------------------------------------------------- */
    bindGlobalEvents() {
      if (this.dom.drawerOverlay) {
        this.dom.drawerOverlay.addEventListener('click', () => this.closeDrawer());
      }
      if (this.dom.drawerClose) {
        this.dom.drawerClose.addEventListener('click', () => this.closeDrawer());
      }
      if (this.dom.drawerToggle) {
        this.dom.drawerToggle.addEventListener('click', () => this.toggleDrawer());
        this.dom.drawerToggle.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggleDrawer();
          }
        });
      }
      if (this.dom.heroCta) {
        this.dom.heroCta.addEventListener('click', e => {
          e.preventDefault();
          const target = document.getElementById('bundle-builder-section');
          if (target) target.scrollIntoView({ behavior: 'smooth' });
        });
      }
      if (this.dom.sidebarClear) {
        this.dom.sidebarClear.addEventListener('click', () => this.clearBundle());
      }

      document.querySelectorAll('.pnh-bb-checkout-btn').forEach(btn => {
        btn.addEventListener('click', () => this.handleCheckout(btn));
      });

      // Swipe-to-close on mobile drawer
      if (this.dom.drawer) this.bindSwipeClose(this.dom.drawer);
    }

    bindSwipeClose(el) {
      let startY = 0;
      el.addEventListener('touchstart', e => { startY = e.touches[0].clientY; }, { passive: true });
      el.addEventListener('touchend', e => {
        const delta = e.changedTouches[0].clientY - startY;
        if (delta > 80) this.closeDrawer();
      }, { passive: true });
    }

    /* -------------------------------------------------------------------------
       Drawer helpers
       ------------------------------------------------------------------------- */
    toggleDrawer() {
      if (this.dom.drawer && this.dom.drawer.classList.contains('pnh-bb-drawer--open')) {
        this.closeDrawer();
      } else {
        this.openDrawer();
      }
    }

    openDrawer() {
      if (!this.dom.drawer) return;
      this._drawerTrigger = document.activeElement;
      this.dom.drawer.classList.add('pnh-bb-drawer--open');
      document.body.classList.add('pnh-bb-drawer-open');
      if (this.dom.drawerToggle) this.dom.drawerToggle.setAttribute('aria-expanded', 'true');
      if (typeof trapFocus === 'function') trapFocus(this.dom.drawer);
    }

    closeDrawer() {
      if (!this.dom.drawer) return;
      this.dom.drawer.classList.remove('pnh-bb-drawer--open');
      document.body.classList.remove('pnh-bb-drawer-open');
      if (this.dom.drawerToggle) this.dom.drawerToggle.setAttribute('aria-expanded', 'false');
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      if (this._drawerTrigger && typeof this._drawerTrigger.focus === 'function') {
        this._drawerTrigger.focus();
      }
    }

    /* -------------------------------------------------------------------------
       Checkout
       ------------------------------------------------------------------------- */
    async handleCheckout(btn) {
      if (this.bundle.size === 0) return;
      btn.disabled = true;
      btn.classList.add('pnh-bb-checkout-btn--loading');
      const origText = btn.textContent;
      btn.textContent = this.t('adding', 'Adding to cart…');

      const items = Array.from(this.bundle.values()).map(item => ({
        id: item.variantId,
        quantity: item.qty,
      }));

      // Resolve the tier code BEFORE any state is cleared. getDiscountCode()
      // iterates this.bundle to count items; clearing the bundle first would
      // collapse the count to 0, no milestone would match, and the
      // navigation would fall back to plain /checkout with no discount.
      const rawCode = this.getDiscountCode();
      const code    = rawCode ? rawCode.trim() : null;
      const target  = code
        ? `/discount/${encodeURIComponent(code)}?redirect=/checkout`
        : '/checkout';

      try {
        const _addCtrl = new AbortController();
        const _addTimeout = setTimeout(() => _addCtrl.abort(), 10000);
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
          signal: _addCtrl.signal,
        });
        clearTimeout(_addTimeout);
        if (!addRes.ok) throw new Error(`Cart error ${addRes.status}`);

        // Items are now in the Shopify cart. Clear local state synchronously
        // before navigating so a back-navigation or missed pagehide on mobile
        // cannot re-submit the same items and duplicate cart quantities.
        this.bundle.clear();
        this.persistToStorage();

        // Wipe the saved code that pnh-cart-premium.js auto-reapplies on any
        // [name="checkout"] button — without this it would replay PNH10 (or
        // whatever the customer last applied on the cart drawer) into the
        // checkout URL after we navigate.
        try { sessionStorage.removeItem('pnh_discount_code'); } catch (_) {}

        // /discount/CODE?redirect=/checkout is Shopify's documented and
        // empirically working pattern — it's what the product-page bundle
        // uses successfully. The redirect= path must be unencoded; Shopify's
        // handler does not reliably decode %2F before following.
        // The 300ms delay gives Shopify's cart backend time to commit the
        // just-added items before /discount/ validates the cart conditions
        // (BUNDLECARE10 needs 4, BUNDLECARE15 needs 8). Without it, the
        // discount can be rejected because /discount/ runs against a cart
        // snapshot that pre-dates /cart/add.js completing on the server.
        setTimeout(() => { window.location.href = target; }, 300);
      } catch (err) {
        console.error('[PNH Bundle] Checkout error:', err);
        btn.disabled = false;
        btn.classList.remove('pnh-bb-checkout-btn--loading');
        btn.textContent = this.t('errorGeneric', 'Something went wrong — please try again');
        setTimeout(() => { btn.textContent = origText; }, 3500);
      }
    }

    /* -------------------------------------------------------------------------
       UI update pipeline
       ------------------------------------------------------------------------- */
    updateUI(animate) {
      this.updateCards();
      this.updateRoutines();
      this.updateSidebar();
      this.updateDrawer();
      this.updateProgress(animate);
      this.updateToggleBubble();
    }

    updateCards() {
      this.dom.cards.forEach(card => {
        const vid = parseInt(card.dataset.variantId, 10);
        const item = this.bundle.get(vid);
        const inBundle = !!item;

        card.classList.toggle('pnh-bb-card--in-bundle', inBundle);

        const addBtn    = card.querySelector('.pnh-bb-card__add');
        const qtyCtrl   = card.querySelector('.pnh-bb-card__qty-control');
        const qtyVal    = card.querySelector('.pnh-bb-card__qty-value');

        if (addBtn) {
          addBtn.textContent = inBundle ? this.t('inBundle', 'In Bundle ✓') : this.t('addToBundle', 'Add to Bundle');
          addBtn.classList.toggle('pnh-bb-card__add--active', inBundle);
        }
        if (qtyCtrl) qtyCtrl.classList.toggle('pnh-bb-card__qty-control--visible', inBundle);
        if (qtyVal && item) qtyVal.textContent = item.qty;
      });
    }

    updateSidebar() {
      if (!this.dom.sidebarItems) return;

      const count    = this.getItemCount();
      const subtotal = this.getSubtotal();
      const discount = this.getDiscount();
      const discAmt  = Math.round(subtotal * discount);
      const final    = subtotal - discAmt;

      if (this.dom.sidebarCount) {
        this.dom.sidebarCount.textContent = (count === 1 ? this.t('itemsSelectedOne', '__N__ item selected') : this.t('itemsSelectedOther', '__N__ items selected')).replace('__N__', count);
      }

      if (this.dom.sidebarEmpty) {
        this.dom.sidebarEmpty.style.display = this.bundle.size === 0 ? 'flex' : 'none';
      }

      this.dom.sidebarItems.innerHTML = this.renderBundleItems();
      this.bindBundleItemEvents(this.dom.sidebarItems);

      if (this.dom.sidebarTotals) {
        this.dom.sidebarTotals.innerHTML = this.renderTotalsHTML(subtotal, discount, discAmt, final);
      }

      if (this.dom.sidebarChkout) {
        this.dom.sidebarChkout.disabled = this.bundle.size === 0;
        this.dom.sidebarChkout.textContent =
          this.bundle.size === 0 ? this.t('completeCollection', 'Complete Your Collection') : this.t('completeCollectionPrice', 'Complete Your Collection — __P__').replace('__P__', this.formatMoney(final));
      }
    }

    updateDrawer() {
      if (!this.dom.drawerItems) return;

      this.dom.drawerItems.innerHTML = this.renderBundleItems();
      this.bindBundleItemEvents(this.dom.drawerItems);

      const subtotal = this.getSubtotal();
      const discount = this.getDiscount();
      const discAmt  = Math.round(subtotal * discount);
      const final    = subtotal - discAmt;

      if (this.dom.drawerTotals) {
        this.dom.drawerTotals.innerHTML = this.renderTotalsHTML(subtotal, discount, discAmt, final);
      }

      if (this.dom.drawerChkout) {
        this.dom.drawerChkout.disabled = this.bundle.size === 0;
        this.dom.drawerChkout.textContent = this.bundle.size === 0
          ? this.t('completeCollection', 'Complete Your Collection')
          : this.t('completeCollectionPrice', 'Complete Your Collection — __P__').replace('__P__', this.formatMoney(final));
      }
    }

    updateToggleBubble() {
      const count = this.getItemCount();
      if (this.dom.toggleCount) this.dom.toggleCount.textContent = count;
      if (this.dom.togglePrice) {
        const final = this.getSubtotal() - Math.round(this.getSubtotal() * this.getDiscount());
        const priceText = count > 0 ? this.formatMoney(final) : '0 EGP';
        this.dom.togglePrice.textContent = priceText;
        this.dom.togglePrice.setAttribute('aria-label', this.t('totalAria', 'Total: __P__').replace('__P__', priceText));
      }
      if (this.dom.drawerToggle) {
        this.dom.drawerToggle.classList.toggle('pnh-bb-drawer-toggle--has-items', count > 0);
      }
      // The bottom bar says how far the next reward is, not just "tap to view".
      if (this.dom.toggleSub) {
        const next = this.getNextMilestone();
        if (count === 0) {
          this.dom.toggleSub.innerHTML = this.toggleSubDefault;
        } else if (next) {
          const need = next.count - count;
          this.dom.toggleSub.innerHTML = (need === 1 ? this.t('addMoreOneHtml', 'Add <strong>__N__ more item</strong> to unlock <strong>__R__</strong>') : this.t('addMoreOtherHtml', 'Add <strong>__N__ more items</strong> to unlock <strong>__R__</strong>')).replace('__N__', need).replace('__R__', this.escapeHTML(next.reward));
        } else {
          this.dom.toggleSub.innerHTML = this.t('allRewardsHtml', 'Your collection is complete. <strong>All rewards are yours.</strong>');
        }
      }
    }

    updateProgress(animate) {
      const count   = this.getItemCount();
      const percent = this.getProgressPercent();
      const next    = this.getNextMilestone();

      document.querySelectorAll('.pnh-bb-progress-fill').forEach(bar => {
        bar.style.transform = `scaleX(${percent / 100})`;
      });

      document.querySelectorAll('.pnh-bb-progress-text').forEach(el => {
        if (next) {
          const need = next.count - count;
          el.innerHTML = (need === 1 ? this.t('addMoreOneHtml', 'Add <strong>__N__ more item</strong> to unlock <strong>__R__</strong>') : this.t('addMoreOtherHtml', 'Add <strong>__N__ more items</strong> to unlock <strong>__R__</strong>')).replace('__N__', need).replace('__R__', next.reward);
        } else if (count > 0) {
          el.innerHTML = this.t('allRewardsHtml', 'Your collection is complete. <strong>All rewards are yours.</strong>');
        } else {
          el.innerHTML = this.t('progressBanner', 'Add products to unlock rewards as your collection grows');
        }
      });

      const flashedMilestones = new Set();
      document.querySelectorAll('.pnh-bb-milestone').forEach(dot => {
        const mc        = parseInt(dot.dataset.count, 10);
        const wasLocked = !dot.classList.contains('pnh-bb-milestone--unlocked');
        const nowUnlocked = count >= mc;

        dot.classList.toggle('pnh-bb-milestone--unlocked', nowUnlocked);

        if (animate && nowUnlocked && wasLocked) {
          dot.classList.add('pnh-bb-milestone--pop');
          setTimeout(() => dot.classList.remove('pnh-bb-milestone--pop'), 600);
          // Dedupe by count+reward so two milestones at the same threshold
          // each get their own flash, but the same milestone rendered across
          // three DOM surfaces only fires once.
          const flashKey = `${mc}|${dot.dataset.reward}`;
          if (!flashedMilestones.has(flashKey)) {
            flashedMilestones.add(flashKey);
            this.showRewardFlash(dot.dataset.reward || '');
          }
        } else if (!nowUnlocked) {
          delete dot.dataset.animated;
        }
      });

      this.previousItemCount = count;
    }

    showRewardFlash(reward) {
      if (!reward) return;
      const existing = document.querySelector('.pnh-bb-reward-flash');
      if (existing) existing.remove();

      const el = document.createElement('div');
      el.className = 'pnh-bb-reward-flash';
      el.innerHTML = `
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        ${this.t('unlockedHtml', "You've unlocked: <strong>__R__</strong>").replace('__R__', reward)}
      `;
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 3400);

      const announcer = document.getElementById('pnh-bb-announcer');
      if (announcer) {
        announcer.textContent = '';
        const msg = this.t('rewardUnlocked', 'Reward unlocked: __R__').split('__R__').join(reward);
        requestAnimationFrame(() => { announcer.textContent = msg; });
      }
    }

    /* -------------------------------------------------------------------------
       Render helpers
       ------------------------------------------------------------------------- */
    renderBundleItems() {
      if (this.bundle.size === 0) return '';
      let html = '';
      this.bundle.forEach(item => { html += this.renderItem(item); });
      return html;
    }

    renderItem(item) {
      const price = this.formatMoney(item.price * item.qty);
      const imgHtml = item.image
        ? `<img src="${item.image}" alt="${this.escapeAttr(item.imageAlt)}" class="pnh-bb-sidebar-item__img" loading="lazy">`
        : `<div class="pnh-bb-sidebar-item__img-placeholder"></div>`;

      return `
        <div class="pnh-bb-sidebar-item">
          <div class="pnh-bb-sidebar-item__media">${imgHtml}</div>
          <div class="pnh-bb-sidebar-item__info">
            <p class="pnh-bb-sidebar-item__title">${this.escapeHTML(item.title)}</p>
            ${item.subtitle ? `<p class="pnh-bb-sidebar-item__subtitle">${this.escapeHTML(item.subtitle)}</p>` : ''}
            <div class="pnh-bb-sidebar-item__controls">
              <button class="pnh-bb-qty-btn pnh-bb-sidebar-item__qty-minus" type="button" data-vid="${item.variantId}" aria-label="${this.t('qtyDecreaseOf', 'Decrease quantity of __T__').replace('__T__', this.escapeAttr(item.title))}">−</button>
              <span class="pnh-bb-sidebar-item__qty" aria-live="polite" aria-atomic="true">${item.qty}</span>
              <button class="pnh-bb-qty-btn pnh-bb-sidebar-item__qty-plus" type="button" data-vid="${item.variantId}" aria-label="${this.t('qtyIncreaseOf', 'Increase quantity of __T__').replace('__T__', this.escapeAttr(item.title))}">+</button>
            </div>
          </div>
          <div class="pnh-bb-sidebar-item__right">
            <span class="pnh-bb-sidebar-item__price">${price}</span>
            <button class="pnh-bb-sidebar-item__remove" type="button" data-vid="${item.variantId}" aria-label="${this.t('removeOf', 'Remove __T__ from bundle').replace('__T__', this.escapeAttr(item.title))}">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
      `;
    }

    renderDeliveryHTML(final) {
      if (!this.freeThreshold || final <= 0) return '';
      const free = final >= this.freeThreshold;
      return `
        <div class="pnh-bb-sidebar-line pnh-bb-sidebar-line--delivery">
          <span>${this.t('delivery', 'Delivery')}</span>
          <span>${free ? this.t('deliveryFree', 'Free') : this.t('deliveryBands', '80, 190 or 225 EGP by area')}</span>
        </div>
      `;
    }

    renderTotalsHTML(subtotal, discount, discAmt, final) {
      return this.renderPriceLinesHTML(subtotal, discount, discAmt, final) + this.renderDeliveryHTML(final)
        + (discount > 0 ? `<p class="pnh-bb-sidebar-note">${this.t('discountAtCheckout', 'Discount applied at checkout')}</p>` : '');
    }

    renderPriceLinesHTML(subtotal, discount, discAmt, final) {
      if (discount > 0) {
        return `
          <div class="pnh-bb-sidebar-line">
            <span>${this.t('subtotal', 'Subtotal')}</span><span>${this.formatMoney(subtotal)}</span>
          </div>
          <div class="pnh-bb-sidebar-line pnh-bb-sidebar-line--discount">
            <span>${this.t('discountLine', '__PCT__% Bundle Discount').replace('__PCT__', Math.round(discount * 100))}</span>
            <span>−${this.formatMoney(discAmt)}</span>
          </div>
          <div class="pnh-bb-sidebar-line pnh-bb-sidebar-line--total">
            <span>${this.t('total', 'Total')}</span><span>${this.formatMoney(final)}</span>
          </div>
        `;
      }
      return `
        <div class="pnh-bb-sidebar-line pnh-bb-sidebar-line--total">
          <span>${this.t('subtotal', 'Subtotal')}</span><span>${this.formatMoney(subtotal)}</span>
        </div>
      `;
    }

    bindBundleItemEvents(container) {
      container.querySelectorAll('.pnh-bb-sidebar-item__remove').forEach(btn => {
        btn.addEventListener('click', () => this.removeItem(parseInt(btn.dataset.vid, 10)));
      });
      container.querySelectorAll('.pnh-bb-sidebar-item__qty-minus').forEach(btn => {
        btn.addEventListener('click', () => {
          const vid = parseInt(btn.dataset.vid, 10);
          const item = this.bundle.get(vid);
          if (!item) return;
          if (item.qty <= 1) { this.removeItem(vid); return; }
          item.qty--;
          this.bundle.set(vid, item);
          this.persistToStorage();
          this.updateUI(false);
        });
      });
      container.querySelectorAll('.pnh-bb-sidebar-item__qty-plus').forEach(btn => {
        btn.addEventListener('click', () => {
          const vid = parseInt(btn.dataset.vid, 10);
          const item = this.bundle.get(vid);
          if (!item) return;
          item.qty = Math.min(item.qty + 1, 99);
          this.bundle.set(vid, item);
          this.persistToStorage();
          this.updateUI(false);
        });
      });
    }

    /* -------------------------------------------------------------------------
       Calculations
       ------------------------------------------------------------------------- */
    getItemCount() {
      let n = 0;
      this.bundle.forEach(i => { n += i.qty; });
      return n;
    }

    getSubtotal() {
      let total = 0;
      this.bundle.forEach(i => { total += i.price * i.qty; });
      return total;
    }

    // Highest-count unlocked milestone with a non-zero discount wins.
    // Falling back through lower tiers ensures a free-gift milestone (e.g. M3
    // at 6 items with discount_pct=0) does not erase the 10% the customer
    // already earned at M2.
    getDiscount() {
      const count = this.getItemCount();
      let best = null;
      this.milestones.forEach(m => {
        if (count >= m.count && m.discount > 0 && (!best || m.count > best.count)) {
          best = m;
        }
      });
      return best ? best.discount : 0;
    }

    // Highest-count unlocked milestone with a code wins. A free-shipping or
    // free-gift tier with no code does not overwrite a valid code from a lower
    // tier — so M3 (free gift, no code) at 6 items keeps M2's BUNDLECARE10.
    getDiscountCode() {
      const count = this.getItemCount();
      let best = null;
      this.milestones.forEach(m => {
        if (m.code && count >= m.count && (!best || m.count > best.count)) {
          best = m;
        }
      });
      return best ? best.code : null;
    }

    getCurrentMilestone() {
      const count = this.getItemCount();
      let cur = null;
      this.milestones.forEach(m => { if (count >= m.count) cur = m; });
      return cur;
    }

    getNextMilestone() {
      const count = this.getItemCount();
      return this.milestones.find(m => m.count > count) || null;
    }

    getProgressPercent() {
      if (!this.milestones.length) return 0;
      const count = this.getItemCount();
      const last  = this.milestones[this.milestones.length - 1];
      return Math.min((count / last.count) * 100, 100);
    }

    /* -------------------------------------------------------------------------
       Utilities
       ------------------------------------------------------------------------- */
    formatMoney(cents) {
      const amount = cents / 100;
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EGP';
      return `${currency} ${amount.toFixed(2)}`;
    }

    escapeHTML(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    escapeAttr(str) { return this.escapeHTML(str); }

    animateCardAdd(vid) {
      const card = this.el.querySelector(`[data-variant-id="${vid}"]`);
      if (!card) return;
      card.classList.add('pnh-bb-card--adding');
      setTimeout(() => card.classList.remove('pnh-bb-card--adding'), 550);

      const sidebar = document.querySelector('.pnh-bb-sidebar');
      if (sidebar) {
        sidebar.classList.add('pnh-bb-pulse');
        setTimeout(() => sidebar.classList.remove('pnh-bb-pulse'), 500);
      }

      const toggle = this.dom.drawerToggle;
      if (toggle) {
        toggle.classList.add('pnh-bb-pulse');
        setTimeout(() => toggle.classList.remove('pnh-bb-pulse'), 500);
      }
    }
  }

  /* ---------------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------------- */
  function boot() {
    const builderEl = document.querySelector('.pnh-bundle-builder');
    if (builderEl) new BundleBuilder(builderEl);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
